import { Activity } from "lucide-react";

export const PageHeader = ({ dept, title, subtitle, children }) => (
  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between fade-up">
    <div>
      {dept && <p className="text-[11px] font-semibold uppercase tracking-wider text-sky-600">{dept}</p>}
      <h1 data-testid="page-title" className="font-heading text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
  </div>
);

export const TILE_THEMES = [
  { tile: "from-blue-500 to-blue-700 shadow-blue-500/25", dot: "bg-blue-500", head: "from-blue-50", chip: "bg-blue-50 text-blue-600" },
  { tile: "from-emerald-500 to-teal-600 shadow-emerald-500/25", dot: "bg-emerald-500", head: "from-emerald-50", chip: "bg-emerald-50 text-emerald-600" },
  { tile: "from-orange-500 to-orange-600 shadow-orange-500/25", dot: "bg-orange-500", head: "from-orange-50", chip: "bg-orange-50 text-orange-600" },
  { tile: "from-violet-500 to-purple-600 shadow-violet-500/25", dot: "bg-violet-500", head: "from-violet-50", chip: "bg-violet-50 text-violet-600" },
  { tile: "from-rose-500 to-pink-600 shadow-rose-500/25", dot: "bg-rose-500", head: "from-rose-50", chip: "bg-rose-50 text-rose-600" },
  { tile: "from-cyan-500 to-sky-600 shadow-cyan-500/25", dot: "bg-cyan-500", head: "from-cyan-50", chip: "bg-cyan-50 text-cyan-600" },
];
const theme = (i) => TILE_THEMES[(i || 0) % TILE_THEMES.length];

export const Panel = ({ title, right, children, className = "", testId, color }) => (
  <section data-testid={testId} className={`overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-shadow hover:shadow-md ${className}`}>
    {title && (
      <div className={`flex items-center justify-between border-b border-slate-100 bg-gradient-to-r ${theme(color).head} to-white px-3 py-2`}>
        <h3 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-700"><span className={`h-3 w-1 rounded-full ${theme(color).dot}`} />{title}</h3>
        {right}
      </div>
    )}
    {children}
  </section>
);

export const DetailGrid = ({ items, testId }) => {
  const shown = items.filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (!shown.length) return null;
  return (
    <dl data-testid={testId} className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
      {shown.map(([l, v]) => (
        <div key={l} className="min-w-0">
          <dt className="text-[11px] uppercase tracking-wider text-slate-500">{l}</dt>
          <dd className="break-words text-slate-900">{v}</dd>
        </div>
      ))}
    </dl>
  );
};

export const Kpi = ({ label, value, sub, testId, color = 0, delay = 0 }) => (
  <div data-testid={testId} style={{ animationDelay: `${delay}ms` }} className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${theme(color).tile} px-3 py-2 text-white shadow-md transition-transform duration-200 hover:-translate-y-0.5 fade-up`}>
    <span className="pointer-events-none absolute -right-5 -top-5 h-14 w-14 rounded-full bg-white/15" />
    <div className="relative flex items-center justify-between gap-2">
      <p className="truncate text-[10px] font-semibold uppercase tracking-wider text-white/90">{label}</p>
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-white/20"><Activity className="h-3.5 w-3.5" /></span>
    </div>
    <p className="relative font-heading text-lg font-bold leading-tight">{value}</p>
    {sub && <p className="relative truncate text-[11px] text-white/80">{sub}</p>}
  </div>
);
